/**
 * Notes — a Teams-style messaging surface with direct and group chats.
 *
 *   ┌───────────────┬──────────────────────────────────┐
 *   │ FL launch  ②  │  FL launch          ⋯            │
 *   │ Ryan: on it   │  Chris, Ryan, Courtney           │
 *   │ Chris Dains   ├──────────────────────────────────┤
 *   │ You: thanks   │        ┌───────────────────────┐ │
 *   │               │  RK    │ Can you check Total…  │ │  ← them
 *   │               │        └───────────────────────┘ │
 *   │               │  ┌──────────────┐                │
 *   │               │  │ On it        │                │  ← me
 *   │               │  └──────────────┘                │
 *   │               ├──────────────────────────────────┤
 *   │ [ New chat ]  │  [ Write a message…      ] [Send]│
 *   └───────────────┴──────────────────────────────────┘
 *
 * A conversation is a thread; a direct message is just a thread with
 * two people. There's no Sent/Received split — you read a conversation,
 * not a direction.
 *
 * Attribution follows the Teams convention: avatar + name + time above
 * the first message of each run, collapsed on consecutive messages from
 * the same person. Shown in direct messages too — without it a column
 * of bubbles carries no attribution once you scroll back.
 *
 * Group specifics:
 *   * Membership changes render as centred system lines.
 *   * Read receipts are DM-only — "2 of 5 have read this" is noise.
 *
 * Sending is optimistic (see useSendMessage): the message paints
 * immediately at 60% opacity with a "Sending…" note, then the server's
 * row replaces it in place. No refetch on the happy path.
 *
 * Responsive:
 *   * < md — one pane at a time. The list and the thread are separate
 *     screens with a back button; 300px of list plus a transcript fits
 *     neither comfortably.
 *   * >= md — both panes, with the list scaling 260 → 340px.
 *   * Height is a clamp against the viewport, using `dvh` where
 *     supported so the composer isn't hidden under a mobile URL bar.
 *   * < sm — the composer and picker dialogs go full-screen.
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditSquareIcon from '@mui/icons-material/BorderColor';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import SendIcon from '@mui/icons-material/Send';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isRepSurfacePath, useActAsRep as useActAsRepRaw } from '../api/actAs';

import {
  clockTime,
  dayLabel,
  initials,
  isPendingMessage,
  relativeTime,
  useAddParticipants,
  useCreateThread,
  useMarkThreadRead,
  useNoteRecipients,
  useRemoveParticipant,
  useRenameThread,
  useSendMessage,
  useThread,
  useThreads,
  type NoteRecipient,
  type ThreadDetail,
  type ThreadMessage,
  type ThreadSummary,
} from '../api/notes';
import { useAuth } from '../auth/useAuth';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const IVORY = '#fdf9f0';
const HAIRLINE = 'rgba(187, 140, 63, 0.22)';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

/** Mirrors the DB CHECK + Pydantic bound. */
const BODY_MAX = 4000;

/** Fallback height, used when the host page isn't a flex column that
 *  can hand this component a share of the viewport (see `fillParent`).
 *
 *  Deliberately conservative. Viewport arithmetic has to account for
 *  the header, the page heading, container padding AND the footer —
 *  under-subtracting pushes the footer below the fold, which is
 *  exactly what a too-small offset did here. Erring large costs a
 *  little height; erring small hides site chrome. */
const PANE_HEIGHT = {
  xs: 'calc(100vh - 330px)',
  sm: 'calc(100vh - 350px)',
  md: 'clamp(420px, calc(100vh - 420px), 780px)',
} as const;

const PANE_HEIGHT_DVH = {
  xs: 'calc(100dvh - 330px)',
  sm: 'calc(100dvh - 350px)',
  md: 'clamp(420px, calc(100dvh - 420px), 780px)',
} as const;

/** Never let the surface collapse below a usable transcript, even on a
 *  short landscape phone. */
const PANE_MIN_HEIGHT = 380;

/** The list pane scales with the viewport instead of sitting at a
 *  constant 300px — that's a third of a tablet's width but a sliver of
 *  a 27-inch monitor. */
const LIST_WIDTH = { md: 260, lg: 300, xl: 340 } as const;

/** Slim, warm scrollbars. The OS default steel-grey bar is the most
 *  conspicuously un-branded thing on the page once everything else is
 *  polished. Webkit only; other engines fall back gracefully. */
const THIN_SCROLLBAR = {
  '&::-webkit-scrollbar': { width: 8 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(187, 140, 63, 0.28)',
    borderRadius: 8,
    border: '2px solid transparent',
    backgroundClip: 'content-box',
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(187, 140, 63, 0.45)' },
} as const;

export function NotesInbox({
  fillParent = false,
}: {
  /** Take a share of a flex-column parent instead of computing a
   *  height from the viewport.
   *
   *  This is the accurate mode: the shell already reserves space for
   *  the header and footer, so filling what's left guarantees both
   *  stay visible without this component knowing their heights. Set it
   *  only when the parent really is `display: flex; flex-direction:
   *  column` with a definite height — in a plain block parent a flex
   *  child collapses to its content. */
  fillParent?: boolean;
} = {}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeId, setActiveId] = useState<number | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const threadsQuery = useThreads();
  const threads = useMemo(() => threadsQuery.data?.threads ?? [], [threadsQuery.data]);

  // Desktop opens the most recent thread by default — an empty right
  // pane beside a populated list is a dead first impression. Mobile
  // stays on the list, where the thread is a full-screen push.
  useEffect(() => {
    if (isMobile || activeId !== null || threads.length === 0) return;
    setActiveId(threads[0]?.id ?? null);
  }, [isMobile, activeId, threads]);

  // If the active thread disappears (left a group), fall back to the
  // list rather than polling a 404 forever.
  useEffect(() => {
    if (activeId === null || threadsQuery.isPending) return;
    if (!threads.some((t) => t.id === activeId)) setActiveId(null);
  }, [activeId, threads, threadsQuery.isPending]);

  const showList = !isMobile || activeId === null;
  const showThread = !isMobile || activeId !== null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          ...(fillParent
            ? // `minHeight: 0` is what lets a flex child actually shrink;
              // without it the panel refuses to go below its content and
              // the footer gets pushed off again.
              { flex: 1, minHeight: 0 }
            : {
                height: PANE_HEIGHT,
                minHeight: PANE_MIN_HEIGHT,
                // Progressive enhancement: browsers that understand
                // dynamic viewport units get the accurate height,
                // everything else keeps the vh fallback above.
                '@supports (height: 100dvh)': { height: PANE_HEIGHT_DVH },
              }),
          border: '1px solid',
          borderColor: HAIRLINE,
          // Squarer on phones, where a large radius eats usable width.
          borderRadius: { xs: 1.5, sm: 2.5 },
          overflow: 'hidden',
          bgcolor: '#fff',
          // Lifts the whole surface off the page ground — the single
          // change that most separates "a bordered div" from a panel.
          boxShadow: '0 1px 2px rgba(15, 10, 7, 0.03), 0 12px 32px rgba(15, 10, 7, 0.06)',
        }}
      >
        {showList && (
          <ThreadList
            threads={threads}
            loading={threadsQuery.isPending}
            error={threadsQuery.isError}
            activeId={activeId}
            onSelect={setActiveId}
            onNewChat={() => setComposerOpen(true)}
            fullWidth={isMobile}
          />
        )}

        {showThread && (
          <ThreadPane
            threadId={activeId}
            onBack={isMobile ? () => setActiveId(null) : undefined}
            onLeft={() => setActiveId(null)}
          />
        )}
      </Box>

      <NewThreadDialog
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onCreated={(threadId) => {
          setComposerOpen(false);
          setActiveId(threadId);
        }}
      />
    </>
  );
}

// =====================================================================
// Left pane — thread list
// =====================================================================

function ThreadList({
  threads,
  loading,
  error,
  activeId,
  onSelect,
  onNewChat,
  fullWidth,
}: {
  threads: ThreadSummary[];
  loading: boolean;
  error: boolean;
  activeId: number | null;
  onSelect: (id: number) => void;
  onNewChat: () => void;
  fullWidth: boolean;
}) {
  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : LIST_WIDTH,
        flexShrink: 0,
        // Guards the pane against being squeezed by a long thread title
        // in the sibling flex child.
        minWidth: 0,
        borderRight: fullWidth ? 'none' : '1px solid',
        borderColor: HAIRLINE,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: IVORY,
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: HAIRLINE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ fontFamily: FONT_INTER, fontSize: 13, fontWeight: 700, color: INK }}>
          Conversations
        </Typography>
        <Tooltip title="New Conversation">
          <IconButton
            size="small"
            onClick={onNewChat}
            aria-label="New Conversation"
            sx={{ color: GOLD, '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.12)' } }}
          >
            <EditSquareIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', ...THIN_SCROLLBAR }}>
        {loading ? (
          <Stack gap={0.5} sx={{ p: 1.25 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={56} />
            ))}
          </Stack>
        ) : error ? (
          <Alert severity="error" sx={{ m: 1.25, fontFamily: FONT_INTER, fontSize: 12.5 }}>
            Couldn’t load conversations.
          </Alert>
        ) : threads.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 28, color: 'rgba(187, 140, 63, 0.5)', mb: 1 }} />
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 12.5,
                color: 'rgba(15, 10, 7, 0.6)',
                lineHeight: 1.5,
              }}
            >
              No conversations yet. Start one with a teammate or a group.
            </Typography>
            <Button
              size="small"
              onClick={onNewChat}
              sx={{
                mt: 1.5,
                fontFamily: FONT_INTER,
                textTransform: 'none',
                fontWeight: 600,
                color: GOLD,
              }}
            >
              New Conversation
            </Button>
          </Box>
        ) : (
          threads.map((t) => (
            <ThreadRow
              key={t.id}
              thread={t}
              active={t.id === activeId}
              onClick={() => onSelect(t.id)}
            />
          ))
        )}
      </Box>
    </Box>
  );
}

function ThreadRow({
  thread,
  active,
  onClick,
}: {
  thread: ThreadSummary;
  active: boolean;
  onClick: () => void;
}) {
  const unread = thread.unread_count > 0;

  // In a group, the preview needs to say WHO spoke — "on it" from an
  // unknown person is useless. DMs don't, since the row is the person.
  const previewPrefix = thread.last_message_from_me
    ? 'You: '
    : thread.is_group && thread.last_message_sender
      ? `${thread.last_message_sender.display_name.split(' ')[0]}: `
      : '';

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        gap: 1.25,
        alignItems: 'center',
        px: 1.5,
        py: 1.35,
        cursor: 'pointer',
        borderLeft: '3px solid',
        borderLeftColor: active ? GOLD : 'transparent',
        // The active row reads as the pane's "front" surface: it lifts
        // to white and casts a hairline shadow, so the selection is
        // legible without a heavy fill.
        bgcolor: active ? '#fff' : 'transparent',
        boxShadow: active ? '0 1px 3px rgba(15, 10, 7, 0.06)' : 'none',
        transition: 'background-color 160ms ease, box-shadow 160ms ease',
        '&:hover': { bgcolor: active ? '#fff' : 'rgba(187, 140, 63, 0.07)' },
        '&:focus-visible': {
          outline: `2px solid ${GOLD}`,
          outlineOffset: '-2px',
        },
      }}
    >
      <Avatar
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          fontSize: 12.5,
          fontWeight: 700,
          fontFamily: FONT_INTER,
          bgcolor: unread ? 'rgba(187, 140, 63, 0.28)' : 'rgba(15, 10, 7, 0.07)',
          color: unread ? '#7d5a1f' : 'rgba(15, 10, 7, 0.55)',
        }}
      >
        {thread.is_group ? <GroupsIcon sx={{ fontSize: 18 }} /> : initials(thread.title)}
      </Avatar>

      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack direction="row" alignItems="baseline" gap={0.75}>
          <Typography
            noWrap
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13,
              fontWeight: unread ? 700 : 600,
              color: INK,
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {thread.title}
          </Typography>
          {thread.last_message_at && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 10.5,
                color: 'rgba(15, 10, 7, 0.45)',
                flexShrink: 0,
              }}
            >
              {relativeTime(thread.last_message_at)}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap={0.75}>
          <Typography
            noWrap
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 12,
              color: unread ? 'rgba(15, 10, 7, 0.78)' : 'rgba(15, 10, 7, 0.5)',
              fontWeight: unread ? 600 : 400,
              fontStyle: thread.last_message ? 'normal' : 'italic',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {thread.last_message ? `${previewPrefix}${thread.last_message}` : 'No messages yet'}
          </Typography>
          {unread && (
            <Box
              sx={{
                minWidth: 17,
                height: 17,
                px: 0.5,
                borderRadius: '999px',
                bgcolor: GOLD,
                color: '#fff',
                fontFamily: FONT_INTER,
                fontSize: 10,
                fontWeight: 700,
                lineHeight: '17px',
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              {thread.unread_count > 99 ? '99+' : thread.unread_count}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

// =====================================================================
// Right pane — the thread
// =====================================================================

function ThreadPane({
  threadId,
  onBack,
  onLeft,
}: {
  threadId: number | null;
  onBack?: () => void;
  onLeft: () => void;
}) {
  const { user } = useAuth();
  // Same predicate the API client uses to decide whether the request
  // carries the act-as header, so "am I writing as someone else" and
  // "am I reading as someone else" can never disagree.
  const { pathname } = useLocation();
  const storedActAs = useActAsRepRaw();
  const impersonating = isRepSurfacePath(pathname) && storedActAs !== null;
  const threadQuery = useThread(threadId);
  const markRead = useMarkThreadRead();
  const sendMessage = useSendMessage();

  const [draft, setDraft] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const thread = threadQuery.data ?? null;
  const messages = useMemo(() => thread?.messages ?? [], [thread]);

  // Newest message from anyone else — the read watermark only needs
  // advancing when that's ahead of what we've acknowledged.
  const latestFromOthers = useMemo(() => {
    const theirs = messages.filter((m) => m.sender.id !== user?.id && m.kind === 'message');
    return theirs.length > 0 ? (theirs[theirs.length - 1]?.created_at ?? null) : null;
  }, [messages, user?.id]);

  const acknowledged = useRef<string | null>(null);

  // Opening a thread reads it. Keyed on the newest incoming message so
  // the 20s poll doesn't fire a write every cycle, and so a message
  // arriving while the thread is open still marks read.
  //
  // Never while acting as somebody else. This is the only impersonated
  // write that needs no click at all: an admin opening a rep's Team
  // Chat tab would silently clear that rep's unread badge for messages
  // the rep has never seen — destroying the one signal telling them to
  // look. Reading over someone's shoulder must not count as them
  // having read it.
  useEffect(() => {
    if (impersonating) return;
    if (threadId === null || !latestFromOthers) return;
    if (acknowledged.current === `${threadId}:${latestFromOthers}`) return;
    acknowledged.current = `${threadId}:${latestFromOthers}`;
    markRead.mutate(threadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, latestFromOthers, impersonating]);

  // Pin to the newest message whenever the transcript changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Clear the draft when switching threads — carrying half a sentence
  // into another conversation is a good way to send it to the wrong
  // people.
  useEffect(() => {
    setDraft('');
  }, [threadId]);

  // Not gated on `sendMessage.isPending`: with the optimistic render
  // the previous message is already on screen, so blocking the box
  // would stall a fast typist for no reason.
  const canSend = threadId !== null && draft.trim().length > 0;

  const submit = () => {
    if (!canSend || threadId === null) return;
    const body = draft.trim();
    setDraft('');
    sendMessage.mutate(
      {
        threadId,
        body,
        // Lets the message paint immediately instead of waiting on the
        // server to tell us who we are.
        optimisticSender: user
          ? {
              id: user.id,
              display_name: `${user.first_name} ${user.last_name}`.trim() || user.email,
              email: user.email,
            }
          : undefined,
      },
      // Restore the text on failure rather than silently losing it.
      { onError: () => setDraft(body) },
    );
  };

  if (threadId === null) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 3,
          textAlign: 'center',
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 34, color: 'rgba(187, 140, 63, 0.45)' }} />
        <Typography sx={{ fontFamily: FONT_INTER, fontSize: 13.5, color: 'rgba(15, 10, 7, 0.55)' }}>
          Pick a conversation to read it.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        sx={{
          px: { xs: 1.25, sm: 2 },
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: HAIRLINE,
          flexShrink: 0,
          // Opaque on purpose: the transcript below has a gradient
          // ground, and a transparent header would let it bleed
          // through the moment anything sits close to the boundary.
          bgcolor: '#fff',
        }}
      >
        {onBack && (
          <IconButton size="small" onClick={onBack} aria-label="Back to conversations">
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        {thread && (
          <>
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: 11.5,
                fontWeight: 700,
                fontFamily: FONT_INTER,
                bgcolor: 'rgba(187, 140, 63, 0.18)',
                color: '#8a6526',
              }}
            >
              {thread.is_group ? <GroupsIcon sx={{ fontSize: 16 }} /> : initials(thread.title)}
            </Avatar>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography
                noWrap
                sx={{ fontFamily: FONT_INTER, fontSize: 13.5, fontWeight: 700, color: INK }}
              >
                {thread.title}
              </Typography>
              <Typography
                noWrap
                sx={{ fontFamily: FONT_INTER, fontSize: 11, color: 'rgba(15, 10, 7, 0.5)' }}
              >
                {thread.is_group
                  ? thread.participants.map((p) => p.display_name).join(', ')
                  : (thread.participants.find((p) => p.id !== user?.id)?.email ?? '')}
              </Typography>
            </Box>

            {thread.is_group && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  aria-label="Conversation options"
                >
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      setManageOpen(true);
                    }}
                    sx={{ fontFamily: FONT_INTER, fontSize: 13.5 }}
                  >
                    <PersonAddAltIcon sx={{ fontSize: 17, mr: 1 }} />
                    People &amp; settings
                  </MenuItem>
                </Menu>
              </>
            )}
          </>
        )}
      </Stack>

      {/* Transcript */}
      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: { xs: 1.25, sm: 2, md: 2.5 },
          py: { xs: 1.5, sm: 2 },
          // Anchor the transcript to the BOTTOM. A short conversation
          // top-aligned leaves a large void above the composer and
          // reads as an empty page; every chat client sits the last
          // message just above the input.
          //
          // `margin-top: auto` on the first child, NOT
          // `justify-content: flex-end`. Both bottom-align short
          // content, but flex-end overflows a scroll container upward
          // and the overflowing part becomes UNREACHABLE — the earliest
          // messages get pushed above the scrollable area and clipped
          // by the header. The auto-margin collapses to zero once
          // content fills the box, so long threads scroll normally.
          display: 'flex',
          flexDirection: 'column',
          '& > *:first-of-type': { marginTop: 'auto' },
          // Faint warm ground so the white bubbles have something
          // to sit ON rather than dissolving into the panel.
          background:
            'linear-gradient(180deg, rgba(253, 249, 240, 0.55) 0%, rgba(253, 249, 240, 0.25) 100%)',
          ...THIN_SCROLLBAR,
        }}
      >
        {threadQuery.isPending ? (
          <Stack gap={1.5}>
            <Skeleton variant="rounded" height={44} width="60%" />
            <Skeleton variant="rounded" height={44} width="50%" sx={{ ml: 'auto' }} />
            <Skeleton variant="rounded" height={44} width="70%" />
          </Stack>
        ) : threadQuery.isError ? (
          <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
            Couldn’t load this conversation.
          </Alert>
        ) : messages.length === 0 ? (
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13,
              color: 'rgba(15, 10, 7, 0.5)',
              textAlign: 'center',
              mt: 4,
            }}
          >
            No messages yet — say something.
          </Typography>
        ) : (
          messages.map((m, i) => {
            const previous = messages[i - 1];
            const next = messages[i + 1];
            const dayDivider =
              !previous || dayLabel(m.created_at) !== dayLabel(previous.created_at)
                ? dayLabel(m.created_at)
                : null;

            // Last bubble of a run: only that one gets the tucked
            // corner, so a run reads as one grouped block rather than
            // three separate speech bubbles.
            const isRunEnd =
              !next ||
              next.sender.id !== m.sender.id ||
              next.kind === 'system' ||
              dayLabel(next.created_at) !== dayLabel(m.created_at);

            return (
              <MessageRow
                key={m.id}
                message={m}
                mine={m.sender.id === user?.id}
                dayDivider={dayDivider}
                isRunEnd={isRunEnd}
                // Head of a run: a new speaker, the first message after
                // a system line, or the first after a day break — a run
                // must not span a divider, or the name would sit on the
                // wrong side of it. Continuations stay collapsed;
                // repeating the name every line is noise.
                showSender={
                  !previous ||
                  previous.sender.id !== m.sender.id ||
                  previous.kind === 'system' ||
                  dayDivider !== null
                }
              />
            );
          })
        )}
      </Box>

      {/* Composer */}
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: HAIRLINE,
          px: { xs: 1.25, sm: 1.75 },
          py: { xs: 1.25, sm: 1.5 },
          bgcolor: IVORY,
        }}
      >
        <Stack direction="row" gap={1.25} alignItems="flex-end">
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter makes a new line — the
              // convention in every chat client.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Write a message…"
            multiline
            maxRows={5}
            fullWidth
            size="small"
            inputProps={{ maxLength: BODY_MAX }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                fontSize: 13.5,
                borderRadius: '22px',
                px: 1,
                transition: 'box-shadow 180ms ease, border-color 180ms ease',
                '& fieldset': { borderColor: 'rgba(15, 10, 7, 0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(187, 140, 63, 0.45)' },
                // Gold focus ring instead of MUI's default blue —
                // the rest of the app never shows that blue.
                '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(187, 140, 63, 0.15)' },
                '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 1 },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={submit}
            disabled={!canSend}
            aria-label="Send message"
            sx={{
              minWidth: 0,
              width: 40,
              height: 40,
              p: 0,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: GOLD,
              boxShadow: '0 2px 8px rgba(139, 101, 38, 0.28)',
              transition:
                'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
              '&:hover': {
                bgcolor: '#a67c34',
                boxShadow: '0 4px 14px rgba(139, 101, 38, 0.34)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)' },
              '&.Mui-disabled': {
                bgcolor: 'rgba(15, 10, 7, 0.10)',
                color: 'rgba(15, 10, 7, 0.30)',
                boxShadow: 'none',
              },
            }}
          >
            <SendIcon sx={{ fontSize: 17 }} />
          </Button>
        </Stack>
      </Box>

      {thread && (
        <ManageGroupDialog
          open={manageOpen}
          thread={thread}
          onClose={() => setManageOpen(false)}
          onLeft={() => {
            setManageOpen(false);
            onLeft();
          }}
        />
      )}
    </Box>
  );
}

function MessageRow({
  message,
  mine,
  dayDivider,
  showSender,
  isRunEnd,
}: {
  message: ThreadMessage;
  mine: boolean;
  dayDivider: string | null;
  showSender: boolean;
  isRunEnd: boolean;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const canOpenAccounts =
    user?.roles.some((r) => r.name === 'field_rep' || r.name === 'admin') ?? false;

  // Optimistically-rendered message, not yet acknowledged by the server.
  const pending = isPendingMessage(message);

  // Hairlines either side of the label — the standard chat divider.
  // Centred text alone doesn't read as a separator, it reads as a
  // stray caption.
  const divider = dayDivider && (
    <Stack direction="row" alignItems="center" gap={1.5} sx={{ my: 2 }}>
      <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'rgba(187, 140, 63, 0.18)' }} />
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: 'rgba(15, 10, 7, 0.4)',
          flexShrink: 0,
        }}
      >
        {dayDivider}
      </Typography>
      <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'rgba(187, 140, 63, 0.18)' }} />
    </Stack>
  );

  // Membership changes read as narration, not conversation.
  if (message.kind === 'system') {
    return (
      <>
        {divider}
        <Typography
          sx={{
            my: 1,
            textAlign: 'center',
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontStyle: 'italic',
            color: 'rgba(15, 10, 7, 0.45)',
          }}
        >
          {message.body}
        </Typography>
      </>
    );
  }

  return (
    <>
      {divider}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: mine ? 'flex-end' : 'flex-start',
          // Extra breathing room above a new speaker's run, tight
          // within one — the gap is what makes runs read as runs.
          mt: showSender ? 1.5 : 0.25,
          '&:hover .msg-time': { opacity: 1 },
        }}
      >
        {/* Hover timestamp for own messages, in the left margin so it
            never shifts the bubble. */}
        {mine && (
          <Typography
            className="msg-time"
            aria-hidden
            sx={{
              mr: 1,
              flexShrink: 0,
              opacity: 0,
              transition: 'opacity 160ms ease',
              fontFamily: FONT_INTER,
              fontSize: 10,
              color: 'rgba(15, 10, 7, 0.38)',
            }}
          >
            {clockTime(message.created_at)}
          </Typography>
        )}

        {/* Avatar gutter, held open even on continuation messages so a
            run stays aligned under its author's name. */}
        {!mine && (
          <Box sx={{ width: 34, flexShrink: 0, mr: 0.75 }}>
            {showSender && (
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: 10.5,
                  fontWeight: 700,
                  fontFamily: FONT_INTER,
                  color: '#7d5a1f',
                  background:
                    'linear-gradient(140deg, rgba(199, 154, 74, 0.30) 0%, rgba(187, 140, 63, 0.16) 100%)',
                  border: '1px solid rgba(187, 140, 63, 0.22)',
                }}
              >
                {initials(message.sender.display_name)}
              </Avatar>
            )}
          </Box>
        )}

        {/* Percentage keeps bubbles proportional on a narrow pane.
            `fit-content` means this column is as wide as its widest
            child — which on a run's first message is the ATTRIBUTION
            line, not the bubble. Without the column alignment below,
            a short bubble then sits left-aligned inside a wider box
            and its right edge falls short of the continuation bubbles
            underneath it. */}
        <Box
          sx={{
            maxWidth: { xs: '82%', sm: '78%' },
            minWidth: 0,
            width: 'fit-content',
            display: 'flex',
            flexDirection: 'column',
            alignItems: mine ? 'flex-end' : 'flex-start',
          }}
        >
          {/* Attribution line — name + time above the bubble, Teams
              style, on the first message of each run. Shown in direct
              messages too: without it a column of bubbles carries no
              attribution at all, and "you know who you're talking to"
              stops being true the moment you scroll back. */}
          {showSender && (
            <Stack
              direction="row"
              alignItems="baseline"
              gap={0.75}
              sx={{ mb: 0.35, px: 0.25, justifyContent: mine ? 'flex-end' : 'flex-start' }}
            >
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 12,
                  fontWeight: 700,
                  color: INK,
                }}
              >
                {mine ? 'You' : message.sender.display_name}
              </Typography>
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: 10.5, color: 'rgba(15, 10, 7, 0.45)' }}
              >
                {clockTime(message.created_at)}
              </Typography>
              {/* Receipt lives here, not inside the bubble — as a
                  block element in there it wrapped onto its own line
                  and stretched a two-character message into a tall
                  box. DM-only; null in groups. */}
              {mine && message.read_by_other !== null && (
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 10.5,
                    // Desaturated toward the warm palette — a saturated
                    // UI-green next to gold and ivory reads as an alert
                    // rather than a receipt.
                    color: message.read_by_other
                      ? 'rgba(90, 122, 88, 0.9)'
                      : 'rgba(15, 10, 7, 0.35)',
                  }}
                >
                  {message.read_by_other ? 'Read' : 'Sent'}
                </Typography>
              )}
              {pending && (
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 10.5,
                    color: 'rgba(15, 10, 7, 0.35)',
                    fontStyle: 'italic',
                  }}
                >
                  Sending…
                </Typography>
              )}
            </Stack>
          )}

          <Box
            sx={{
              display: 'inline-block',
              px: 1.75,
              py: 1.15,
              // Generous, near-pill corners. Only the LAST bubble of a
              // run tucks its corner toward the speaker, so a run reads
              // as one grouped block with a single tail rather than
              // three bubbles each pointing at the same person.
              borderRadius: '14px',
              ...(isRunEnd
                ? mine
                  ? { borderBottomRightRadius: 5 }
                  : { borderBottomLeftRadius: 5 }
                : {}),
              // Theirs on paper, mine on a warm gold gradient. The
              // gradient (rather than a flat wash) is what stops the
              // bubble reading as a grey box with a tint.
              background: mine
                ? 'linear-gradient(135deg, rgba(199, 154, 74, 0.22) 0%, rgba(187, 140, 63, 0.13) 100%)'
                : '#fff',
              border: '1px solid',
              borderColor: mine ? 'rgba(187, 140, 63, 0.32)' : 'rgba(15, 10, 7, 0.09)',
              boxShadow: mine
                ? '0 1px 2px rgba(139, 101, 38, 0.07), 0 4px 12px rgba(139, 101, 38, 0.05)'
                : '0 1px 2px rgba(15, 10, 7, 0.04), 0 4px 12px rgba(15, 10, 7, 0.035)',
              // A message still in flight sits back until it lands.
              opacity: pending ? 0.6 : 1,
              transition: 'opacity 220ms ease',
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 13.5,
                lineHeight: 1.55,
                color: 'rgba(15, 10, 7, 0.85)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {message.body}
            </Typography>

            {message.account && (
              <Chip
                icon={<StorefrontIcon sx={{ fontSize: 13 }} />}
                label={
                  message.account.state_code
                    ? `${message.account.name} · ${message.account.state_code}`
                    : message.account.name
                }
                size="small"
                // The account page is `field_rep`-gated but this
                // surface isn't — render a plain label rather than a
                // link that would dead-end on Forbidden.
                onClick={
                  canOpenAccounts
                    ? () => navigate(`/regional-sales/accounts/${message.account?.id}`)
                    : undefined
                }
                sx={{
                  mt: 0.75,
                  height: 20,
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  border: '1px solid rgba(187, 140, 63, 0.35)',
                  cursor: canOpenAccounts ? 'pointer' : 'default',
                }}
              />
            )}

            {/* No inline timestamp. Putting one on continuation
                bubbles (but not on the run's first, which has it in
                the header) made bubbles in one run visibly different
                heights — a two-word reply became twice as tall as the
                line above it. Time is revealed in the margin on hover
                via `.msg-time` instead. */}
          </Box>
        </Box>

        {/* Hover timestamp for incoming messages, mirrored to the
            right margin. */}
        {!mine && (
          <Typography
            className="msg-time"
            aria-hidden
            sx={{
              ml: 1,
              flexShrink: 0,
              opacity: 0,
              transition: 'opacity 160ms ease',
              fontFamily: FONT_INTER,
              fontSize: 10,
              color: 'rgba(15, 10, 7, 0.38)',
            }}
          >
            {clockTime(message.created_at)}
          </Typography>
        )}
      </Box>
    </>
  );
}

// =====================================================================
// New conversation
// =====================================================================

function NewThreadDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (threadId: number) => void;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const recipientsQuery = useNoteRecipients(open);
  const createThread = useCreateThread();

  const [selected, setSelected] = useState<NoteRecipient[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recipients = useMemo(() => recipientsQuery.data?.recipients ?? [], [recipientsQuery.data]);

  // One person is a DM; two or more is a group. The form reveals the
  // group-name field only once it's actually a group.
  const isGroup = selected.length > 1;

  const reset = () => {
    setSelected([]);
    setTitle('');
    setBody('');
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = () => {
    if (selected.length === 0) {
      setError('Pick at least one person.');
      return;
    }
    createThread.mutate(
      {
        participant_ids: selected.map((s) => s.id),
        title: isGroup && title.trim() ? title.trim() : null,
        body: body.trim() || null,
      },
      {
        onSuccess: (thread) => {
          reset();
          onCreated(thread.id);
        },
        onError: () => setError('Couldn’t start that conversation. Try again.'),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      // Full-screen on phones: a form with a multi-select picker and a
      // message box doesn't fit in a floating card at 375px, and a
      // cramped modal is where people mis-tap the wrong recipient.
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          border: fullScreen ? 'none' : `1px solid ${HAIRLINE}`,
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: IVORY, borderBottom: `1px solid ${HAIRLINE}`, py: 1.75 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: GOLD,
            mb: 0.25,
          }}
        >
          New Conversation
        </Typography>
        <Typography sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: 16, color: INK }}>
          {isGroup ? 'Start a Group Chat' : 'Message a Teammate'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important' }}>
        <Stack gap={2}>
          {error && (
            <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
              {error}
            </Alert>
          )}

          <RecipientPicker
            recipients={recipients}
            loading={recipientsQuery.isPending}
            value={selected}
            onChange={(v) => {
              setSelected(v);
              setError(null);
            }}
            label="To"
            helperText="Add a second person to make it a group."
          />

          {isGroup && (
            <TextField
              label="Group name (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g. FL launch"
              helperText="Leave blank and it'll be named after the members."
              FormHelperTextProps={{ sx: { fontFamily: FONT_INTER, fontSize: 11 } }}
            />
          )}

          <TextField
            label="First message (optional)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            size="small"
            inputProps={{ maxLength: BODY_MAX }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={close}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            color: 'rgba(15, 10, 7, 0.6)',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={selected.length === 0 || createThread.isPending}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: GOLD,
            '&:hover': { bgcolor: '#a67c34' },
            '&.Mui-disabled': { bgcolor: 'rgba(187, 140, 63, 0.28)', color: '#fff' },
          }}
        >
          {createThread.isPending ? 'Starting…' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// =====================================================================
// Group management
// =====================================================================

function ManageGroupDialog({
  open,
  thread,
  onClose,
  onLeft,
}: {
  open: boolean;
  thread: ThreadDetail;
  onClose: () => void;
  onLeft: () => void;
}) {
  const { user } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const recipientsQuery = useNoteRecipients(open);
  const addParticipants = useAddParticipants();
  const removeParticipant = useRemoveParticipant();
  const renameThread = useRenameThread();

  const [toAdd, setToAdd] = useState<NoteRecipient[]>([]);
  const [title, setTitle] = useState(thread.title);

  // Only offer people who aren't already in the group.
  const candidates = useMemo(() => {
    const present = new Set(thread.participants.map((p) => p.id));
    return (recipientsQuery.data?.recipients ?? []).filter((r) => !present.has(r.id));
  }, [recipientsQuery.data, thread.participants]);

  useEffect(() => {
    setTitle(thread.title);
  }, [thread.title]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          border: fullScreen ? 'none' : `1px solid ${HAIRLINE}`,
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: IVORY, borderBottom: `1px solid ${HAIRLINE}`, py: 1.75 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: GOLD,
            mb: 0.25,
          }}
        >
          Group
        </Typography>
        <Typography sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: 16, color: INK }}>
          People &amp; Settings
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important' }}>
        <Stack gap={2.5}>
          {/* Rename — creator only, per the API rule surfaced as
              `can_manage`. */}
          {thread.can_manage && (
            <Stack direction="row" gap={1} alignItems="flex-start">
              <TextField
                label="Group name"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                size="small"
                fullWidth
              />
              <Button
                onClick={() =>
                  renameThread.mutate({ threadId: thread.id, title: title.trim() || null })
                }
                disabled={renameThread.isPending || title === thread.title}
                sx={{
                  mt: 0.25,
                  fontFamily: FONT_INTER,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: GOLD,
                }}
              >
                Save
              </Button>
            </Stack>
          )}

          <Box>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11,
                fontWeight: 700,
                color: 'rgba(15, 10, 7, 0.5)',
                mb: 0.75,
              }}
            >
              In this conversation ({thread.participants.length})
            </Typography>
            <Stack gap={0.5}>
              {thread.participants.map((p) => {
                const isMe = p.id === user?.id;
                // Anyone can leave; only the creator can remove others.
                const canRemove = isMe || thread.can_manage;
                return (
                  <Stack key={p.id} direction="row" alignItems="center" gap={1.25}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: FONT_INTER,
                        bgcolor: 'rgba(187, 140, 63, 0.18)',
                        color: '#8a6526',
                      }}
                    >
                      {initials(p.display_name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography
                        noWrap
                        sx={{ fontFamily: FONT_INTER, fontSize: 13, fontWeight: 600, color: INK }}
                      >
                        {p.display_name}
                        {isMe && ' (you)'}
                        {thread.created_by === p.id && (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.75,
                              fontSize: 10,
                              fontWeight: 700,
                              color: GOLD,
                            }}
                          >
                            OWNER
                          </Box>
                        )}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{ fontFamily: FONT_INTER, fontSize: 11, color: 'rgba(15,10,7,0.5)' }}
                      >
                        {p.email}
                      </Typography>
                    </Box>
                    {canRemove && (
                      <Button
                        size="small"
                        onClick={() =>
                          removeParticipant.mutate(
                            { threadId: thread.id, userId: p.id },
                            { onSuccess: () => (isMe ? onLeft() : undefined) },
                          )
                        }
                        disabled={removeParticipant.isPending}
                        sx={{
                          fontFamily: FONT_INTER,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: 12,
                          color: isMe ? '#b22f2f' : 'rgba(15, 10, 7, 0.55)',
                        }}
                      >
                        {isMe ? 'Leave' : 'Remove'}
                      </Button>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11,
                fontWeight: 700,
                color: 'rgba(15, 10, 7, 0.5)',
                mb: 0.75,
              }}
            >
              Add people
            </Typography>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <Box sx={{ flexGrow: 1 }}>
                <RecipientPicker
                  recipients={candidates}
                  loading={recipientsQuery.isPending}
                  value={toAdd}
                  onChange={setToAdd}
                  label="Add to group"
                  helperText="They'll see the full history."
                />
              </Box>
              <Button
                onClick={() =>
                  addParticipants.mutate(
                    { threadId: thread.id, participant_ids: toAdd.map((t) => t.id) },
                    { onSuccess: () => setToAdd([]) },
                  )
                }
                disabled={toAdd.length === 0 || addParticipants.isPending}
                sx={{
                  mt: 0.25,
                  fontFamily: FONT_INTER,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: GOLD,
                }}
              >
                Add
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            color: 'rgba(15, 10, 7, 0.6)',
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// =====================================================================
// Shared people picker
// =====================================================================

function RecipientPicker({
  recipients,
  loading,
  value,
  onChange,
  label,
  helperText,
}: {
  recipients: NoteRecipient[];
  loading: boolean;
  value: NoteRecipient[];
  onChange: (value: NoteRecipient[]) => void;
  label: string;
  helperText?: string;
}) {
  return (
    <Autocomplete
      multiple
      options={recipients}
      value={value}
      loading={loading}
      getOptionLabel={(o) => o.display_name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      // The backend already returns HY staff first; groupBy just
      // labels the boundary — MUI groups in list order.
      groupBy={(o) => (o.is_priority ? 'Hooten Young' : 'Other')}
      onChange={(_, v) => onChange(v)}
      slotProps={{
        paper: {
          sx: {
            border: `1px solid ${HAIRLINE}`,
            '& .MuiAutocomplete-listbox': { maxHeight: 240, py: 0 },
          },
        },
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              px: 1.75,
              py: 0.55,
              bgcolor: IVORY,
              borderBottom: `1px solid ${HAIRLINE}`,
              fontFamily: FONT_INTER,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.03em',
              color: GOLD,
            }}
          >
            {params.group}
          </Box>
          <Box component="ul" sx={{ p: 0 }}>
            {params.children}
          </Box>
        </li>
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id} sx={{ py: 0.6 }}>
          <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                width: 26,
                height: 26,
                flexShrink: 0,
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: FONT_INTER,
                bgcolor: 'rgba(187, 140, 63, 0.18)',
                color: '#8a6526',
              }}
            >
              {initials(option.display_name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: 13.5, fontWeight: 600, color: INK }}
              >
                {option.display_name}
              </Typography>
              <Typography
                noWrap
                sx={{ fontFamily: FONT_INTER, fontSize: 11.5, color: 'rgba(15,10,7,0.5)' }}
              >
                {option.email}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? 'Start typing a name…' : ''}
          size="small"
          helperText={helperText}
          FormHelperTextProps={{ sx: { fontFamily: FONT_INTER, fontSize: 11 } }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
